import { useEffect, useRef, useState } from 'react';
import type { IconCategory, MapObject } from '@warplan/shared';
import {
  ICON_CATALOG,
  ORDERED_CATEGORIES,
  getIconEntry,
} from '../lib/icon-catalog.js';
import { IconPalette } from './IconPalette.js';

export interface MapObjectPopoverProps {
  /** Screen-space anchor (client coordinates). */
  x: number;
  y: number;
  object: MapObject;
  canEdit: boolean;
  onPatch: (patch: { kind?: IconCategory; metadata?: MapObject['metadata'] }) => void;
  onDelete: () => void;
  onClose: () => void;
  /** Categories that are entirely off-limits for the current user (change-category mode). */
  forbiddenCategories?: ReadonlySet<IconCategory> | undefined;
  /** Icon IDs that are off-limits in the icon palette (change-icon mode). */
  forbiddenIconIds?: ReadonlySet<string> | undefined;
}

const POPOVER_W = 280;

export function MapObjectPopover({
  x,
  y,
  object,
  canEdit,
  onPatch,
  onDelete,
  onClose,
  forbiddenCategories,
  forbiddenIconIds,
}: MapObjectPopoverProps): React.JSX.Element {
  const [mode, setMode] = useState<'view' | 'change-icon' | 'change-category'>('view');
  const [note, setNote] = useState(object.metadata.note ?? '');
  const rootRef = useRef<HTMLDivElement | null>(null);
  // noteRef tracks the LAST SAVED value (for dirty detection).
  const noteRef = useRef(object.metadata.note ?? '');
  // noteValueRef tracks the CURRENT TYPED value (always up-to-date, no closure staleness).
  const noteValueRef = useRef(object.metadata.note ?? '');

  // Sync local note state when the object changes (e.g. SSE update from peer).
  useEffect(() => {
    setNote(object.metadata.note ?? '');
    noteRef.current = object.metadata.note ?? '';
    noteValueRef.current = object.metadata.note ?? '';
  }, [object.id, object.metadata.note]);

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        commitNoteIfChanged();
        onClose();
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        commitNoteIfChanged();
        onClose();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  function commitNoteIfChanged(): void {
    if (!canEdit) return;
    // Read noteValueRef (always current) rather than the potentially-stale `note` closure.
    const current = noteValueRef.current;
    if (current === noteRef.current) return;
    onPatch({
      metadata: {
        iconId: object.metadata.iconId,
        ...(current ? { note: current } : {}),
      },
    });
    noteRef.current = current;
  }

  const entry = getIconEntry(object.kind, object.metadata.iconId);
  const vpW = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vpH = typeof window !== 'undefined' ? window.innerHeight : 768;
  const left = Math.min(x, vpW - POPOVER_W - 8);
  const top = Math.min(y, vpH - 240);

  return (
    <div
      ref={rootRef}
      className="fixed z-40 rounded border border-slate-700 bg-slate-900/95 shadow-xl backdrop-blur"
      style={{ left, top, width: POPOVER_W }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-slate-700 px-3 py-2">
        <img src={entry.src} alt="" className="h-6 w-6 object-contain" />
        <div className="flex-1 truncate text-sm font-medium text-slate-100">{entry.label}</div>
        <button
          type="button"
          onClick={() => {
            commitNoteIfChanged();
            onClose();
          }}
          className="rounded px-1 text-slate-400 hover:text-slate-100"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      {mode === 'view' && (
        <div className="space-y-2 px-3 py-2">
          <label className="block text-xs text-slate-400">
            Note
            <textarea
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                noteValueRef.current = e.target.value;
              }}
              disabled={!canEdit}
              maxLength={2000}
              rows={3}
              placeholder={canEdit ? 'Add a note…' : 'Read-only'}
              className="mt-1 block w-full rounded bg-slate-800 px-2 py-1 text-sm text-slate-100 outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-60"
            />
          </label>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setMode('change-icon')}
                className="rounded bg-slate-700 px-2 py-1 text-xs text-slate-100 hover:bg-slate-600"
              >
                Change icon
              </button>
              <button
                type="button"
                onClick={() => setMode('change-category')}
                className="rounded bg-slate-700 px-2 py-1 text-xs text-slate-100 hover:bg-slate-600"
              >
                Change category…
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                className="rounded bg-slate-800 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}

      {mode === 'change-icon' && (
        <div className="px-2 py-2">
          <div className="mb-1 flex items-center justify-between px-1 text-xs text-slate-400">
            <span>Pick a {ICON_CATALOG[object.kind].label} icon</span>
            <button
              type="button"
              onClick={() => setMode('view')}
              className="text-slate-300 hover:text-slate-100"
            >
              ← back
            </button>
          </div>
          <IconPalette
            category={object.kind}
            selectedIconId={object.metadata.iconId}
            onPick={(iconId) => {
              onPatch({
                metadata: {
                  iconId,
                  ...(object.metadata.note ? { note: object.metadata.note } : {}),
                },
              });
              setMode('view');
            }}
            forbiddenIconIds={forbiddenIconIds}
          />
        </div>
      )}

      {mode === 'change-category' && (
        <div className="space-y-1 px-2 py-2 text-sm">
          <div className="mb-1 flex items-center justify-between px-1 text-xs text-slate-400">
            <span>Pick a category</span>
            <button
              type="button"
              onClick={() => setMode('view')}
              className="text-slate-300 hover:text-slate-100"
            >
              ← back
            </button>
          </div>
          <ul>
            {ORDERED_CATEGORIES.map((cat) => {
              const catForbidden = forbiddenCategories?.has(cat) ?? false;
              return (
              <li key={cat}>
                <button
                  type="button"
                  disabled={catForbidden}
                  onClick={() => {
                    if (catForbidden) return;
                    if (cat === object.kind) {
                      setMode('view');
                      return;
                    }
                    onPatch({
                      kind: cat,
                      metadata: { iconId: ICON_CATALOG[cat].defaultIconId },
                    });
                    setMode('view');
                  }}
                  className={
                    'flex w-full items-center gap-2 rounded px-2 py-1 text-left ' +
                    (catForbidden
                      ? 'opacity-30 cursor-not-allowed'
                      : 'hover:bg-slate-700/70 ' + (cat === object.kind ? 'ring-1 ring-emerald-500' : ''))
                  }
                >
                  <img
                    src={getIconEntry(cat, ICON_CATALOG[cat].defaultIconId).src}
                    alt=""
                    className="h-5 w-5"
                  />
                  <span>{ICON_CATALOG[cat].label}</span>
                </button>
              </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
